import React, { createContext, useContext, useState } from "react";

import { useRouter } from "next/router";

import useChallengeArticleContext from "@/challenges/utils/useChallengeArticleContext";
import useIsAuthorizedForIndependentReadingChallenge from "@/challenges/utils/useIsAuthorizedForIndependentReadingChallenge";
import useHasFeature from "@/features/useHasFeature";
import { Assignment } from "@/types/models/Assignment.interface";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import activityTypes from "../../constants/activityTypes/activityTypes.constants";
import getAssignmentSetting from "../getAssignmentSetting";
import useGuidedHighlightingFeedbackGet from "../useGuidedHighlightingFeedbackGet";
import useGuidedHighlightingGetAnswers from "../useGuidedHighlightingGetAnswers";
import useGuidedHighlightingParnassusPost from "../useGuidedHighlightingParnassusPost/useGuidedHighlightingParnassusPost";
import {
  GUIDED_HIGHLIGHTING_QUESTION_STATUSES,
  IAttachment,
  IGuidedHighlightingFeedback,
  IGuidedHighlightingLevel,
  IGuidedHighlightingQuestion,
  IPreviousAnswer,
} from "./types";
import { convertResponse, updateQuestionData } from "./utils";

interface IGuidedHighlightingStudentContext {
  guidedHighlightingData: IGuidedHighlightingLevel | undefined;
  activeGuidedHighlightingQuestion: IGuidedHighlightingQuestion | undefined;
  getGuidedHighlightingData: (
    attachments: IAttachment[] | null,
  ) => Promise<IGuidedHighlightingLevel | undefined>;
  setGuidedHighlightingData: (
    newData: IGuidedHighlightingLevel | undefined,
  ) => void;
  handleActiveQuestion: (
    question: IGuidedHighlightingQuestion | undefined,
  ) => void;
  handleNextQuestion: () => void;
  handleCompleteActivity: (data?: IGuidedHighlightingLevel | undefined) => void;
  isVisible: (assignment: Assignment) => boolean;
  isGuidedHighlightingEnabled: (assignment: Assignment) => boolean;
  isFeatureFlagActive: () => boolean;
  isCompleted: boolean;
  setActiveQuestion: (
    question: IGuidedHighlightingQuestion | undefined,
  ) => void;
  handleSaveProgress: () => number | null;
  shouldShowIRCTokens: boolean;
}
const GuidedHighlightingStudentContext = createContext<
  IGuidedHighlightingStudentContext | undefined
>(undefined);

export const GuidedHighlightingStudentContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [guidedHighlightingData, setGuidedHighlightingData] = useState<
    IGuidedHighlightingLevel | undefined
  >();
  const [activeGuidedHighlightingQuestion, setActiveQuestion] = useState<
    IGuidedHighlightingQuestion | undefined
  >();
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const { data: user } = useUser();
  const { isTeacher } = getUserProperties(user, { shouldUseNewUserApi: false });
  const [getAnswers] = useGuidedHighlightingGetAnswers();
  const [getReports] = useGuidedHighlightingFeedbackGet();
  const [parnassusPostAnswer] = useGuidedHighlightingParnassusPost();

  const [canViewGuidedHighlighting] = useHasFeature(
    "can_view_guided_highlighting",
  );
  const router = useRouter();
  const {
    query: { levelId },
  } = router;
  const { articleData, refetch: refetchChallengeArticle } =
    useChallengeArticleContext();
  const isAuthorizedChallengesPageUser =
    useIsAuthorizedForIndependentReadingChallenge();

  const getReportedQuestions = async () => {
    try {
      const questionsIdList = guidedHighlightingData?.questions
        ?.map((q) => q.id)
        .join(",");
      if (!questionsIdList) return [];
      const response = await getReports({ questionsIdList });
      if (!response?.data) return [];
      return (
        response?.data?.map(
          (feedback: IGuidedHighlightingFeedback) => feedback.contentId,
        ) || []
      );
    } catch (error) {
      console.error("Error fetching reported list:", error);
      return [];
    }
  };

  const getPreviousAnswers = async () => {
    try {
      const response = await getAnswers({ articleId: levelId });
      return response?.data as IPreviousAnswer[];
    } catch (error) {
      console.error("Error fetching previous answers:", error);
      return [];
    }
  };

  const getGuidedHighlightingData = async (
    attachments: IAttachment[] | null,
  ) => {
    if (!attachments) return;

    const attachment = attachments.find(
      ({ data: { assessment_type } }) =>
        assessment_type === activityTypes.GUIDED_HIGHLIGHTING_QUIZ.objectType,
    );

    const dataByLevel = attachment?.data.levels.find(
      ({ article_level_id }) => levelId === article_level_id,
    );

    let previousAnswers = Array<IPreviousAnswer>();

    if (!isTeacher) {
      previousAnswers = await getPreviousAnswers();
    }

    const reportedList = await getReportedQuestions();
    if (dataByLevel) {
      dataByLevel.questions = updateQuestionData(
        dataByLevel.questions,
        reportedList,
        previousAnswers,
      );
      const camelCaseDataByLevel = convertResponse(dataByLevel);
      setGuidedHighlightingData(camelCaseDataByLevel);
    }
    return dataByLevel;
  };

  const handleActiveQuestion = (
    question: IGuidedHighlightingQuestion | undefined,
  ) =>
    setActiveQuestion((prev) =>
      prev?.id === question?.id ? undefined : question,
    );

  const handleNextQuestion = () => {
    if (guidedHighlightingData?.questions.length) {
      const currentIndex = guidedHighlightingData.questions.findIndex(
        ({ readingSkill }) =>
          readingSkill === activeGuidedHighlightingQuestion?.readingSkill,
      );
      const nextQuestion =
        guidedHighlightingData.questions[
          (currentIndex + 1) % guidedHighlightingData.questions.length
        ];
      handleActiveQuestion(nextQuestion);
    }
  };

  const handleCompleteActivity = (
    data?: IGuidedHighlightingLevel | undefined,
  ) => {
    const verificationData = data || guidedHighlightingData;
    const allQuestionsCompleted = verificationData?.questions?.every(
      ({ status }) =>
        status === GUIDED_HIGHLIGHTING_QUESTION_STATUSES.completed,
    );
    setIsCompleted(allQuestionsCompleted || false);
    allQuestionsCompleted && setActiveQuestion(undefined);
  };

  const handleSaveProgress = () => {
    if (!guidedHighlightingData?.questions.length) return null;

    const score = guidedHighlightingData.questions.reduce(
      (acc, question) => acc + (question.tokensEarned || 0),
      0,
    );
    parnassusPostAnswer({
      student_id: user?.unique_id,
      student_grade_level: user?.grade_level,
      interaction_id: guidedHighlightingData?.id,
      interaction_type: "guided_highlighting",
      score,
      has_guided_highlighting: true,
      has_power_words: true,
    }).then(() => {
      refetchChallengeArticle?.();
    });

    return score;
  };

  const shouldShowIRCTokens =
    isAuthorizedChallengesPageUser && articleData?.data !== undefined;

  const isGuidedHighlightingEnabled = (assignment: Assignment) =>
    assignment && !getAssignmentSetting("hide_guided_highlighting", assignment);

  const isFeatureFlagActive = () => canViewGuidedHighlighting;
  const isVisible = (assignment: Assignment) =>
    (guidedHighlightingData?.questions?.length || 0) > 0 &&
    isGuidedHighlightingEnabled(assignment) &&
    isFeatureFlagActive();

  return (
    <GuidedHighlightingStudentContext.Provider
      value={{
        guidedHighlightingData,
        getGuidedHighlightingData,
        isVisible,
        isGuidedHighlightingEnabled,
        isFeatureFlagActive,
        activeGuidedHighlightingQuestion,
        setActiveQuestion,
        handleActiveQuestion,
        isCompleted,
        handleCompleteActivity,
        handleNextQuestion,
        handleSaveProgress,
        setGuidedHighlightingData,
        shouldShowIRCTokens,
      }}
    >
      {children}
    </GuidedHighlightingStudentContext.Provider>
  );
};

const useGuidedHighlightingStudent = (): IGuidedHighlightingStudentContext => {
  const context = useContext(GuidedHighlightingStudentContext);
  if (context === undefined) {
    throw new Error(
      "useGuidedHighlightingStudent must be used within a GuidedHighlightingStudentContextProvider.",
    );
  }
  return context;
};

export default useGuidedHighlightingStudent;
